import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ClipboardCheck, Save, Send, Sparkles, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QuestionRenderer } from "@/components/lift/QuestionRenderer";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";
import LiftPacketPanel from "@/components/rhythm/LiftPacketPanel";

export default function ManagerEvaluationForm() {
  const { cycleId, employeeId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [strengths, setStrengths] = useState("");
  const [opportunities, setOpportunities] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/lift/manager-evaluation/${cycleId}/${employeeId}`);
      setState(data);
      setAnswers(data.answers || []);
      setStrengths(data.strengths || "");
      setOpportunities(data.opportunities || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, employeeId]);

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      await api.put(`/lift/manager-evaluation/${cycleId}/${employeeId}`, {
        answers,
        strengths,
        opportunities,
        status: nextStatus,
      });
      toast.success(nextStatus === "submitted" ? "Manager evaluation submitted." : "Draft saved.");
      if (nextStatus === "submitted") {
        setState((s) => ({ ...s, status: "submitted", readonly: true }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!state) return <p className="italic text-charcoal-muted">Loading…</p>;
  const readonly = state.readonly;

  return (
    <div className="space-y-6" data-testid="lift-manager-eval">
      <Link to={`/lift/manager/${cycleId}`} className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Back to reports
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">
          Manager Evaluation · {state.employee_name}
        </h1>
        <p className="text-sm text-charcoal-muted">{state.employee_title || "—"}</p>
        {readonly && <span className="patria-chip-green mt-2 inline-block">Submitted — read-only</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <button
          className="patria-card patria-card-hover p-5 text-left"
          onClick={() => navigate(`/lift/peer-requests/${cycleId}?forEmployeeId=${employeeId}`)}
          data-testid="mgr-request-peers"
        >
          <span className="patria-chip-terra">Manager-requested</span>
          <h3 className="mt-2 text-lg font-serif text-forest-900">Request Peer Feedback</h3>
          <p className="mt-1 text-sm text-charcoal-soft">
            Invite colleagues who see this teammate&rsquo;s work regularly.
          </p>
          <p className="mt-2 inline-flex items-center text-forest-600 text-sm font-medium">
            <UserPlus className="mr-1 h-4 w-4" strokeWidth={1.5} /> Pick peers
          </p>
        </button>
        <button
          className="patria-card patria-card-hover p-5 text-left"
          onClick={() => navigate(`/lift/synthesis/${cycleId}/${employeeId}`)}
          data-testid="mgr-open-synthesis"
        >
          <span className="patria-chip-green">Synthesis</span>
          <h3 className="mt-2 text-lg font-serif text-forest-900">Build The Final Packet</h3>
          <p className="mt-1 text-sm text-charcoal-soft">
            Assemble the packet, generate an AI draft, edit, and finalize.
          </p>
        </button>
        <div className="patria-card p-5">
          <span className="patria-chip-neutral">Employee</span>
          <h3 className="mt-2 text-lg font-serif text-forest-900">Self-Reflection</h3>
          <p className="mt-1 text-sm text-charcoal-soft">
            Visible in the synthesis view. Encourage them warmly if it&rsquo;s not yet submitted.
          </p>
        </div>
      </div>

      <LiftPacketPanel cycleId={cycleId} employeeId={employeeId} />

      <div className="patria-card p-6 sm:p-8 space-y-6">
        <QuestionRenderer
          template={state.template}
          answers={answers}
          onChange={setAnswers}
          disabled={readonly || saving}
        />

        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
            Strengths narrative
          </label>
          <textarea
            className="patria-input w-full min-h-[110px]"
            value={strengths}
            onChange={(e) => setStrengths(e.target.value)}
            disabled={readonly || saving}
            data-testid="mgr-strengths"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
            Growth opportunities narrative
          </label>
          <textarea
            className="patria-input w-full min-h-[110px]"
            value={opportunities}
            onChange={(e) => setOpportunities(e.target.value)}
            disabled={readonly || saving}
            data-testid="mgr-opportunities"
          />
        </div>
      </div>

      {!readonly && (
        <div className="flex justify-end gap-3">
          <button
            className="patria-btn-secondary"
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="mgr-save-draft"
          >
            <Save className="h-4 w-4" strokeWidth={1.5} /> Save draft
          </button>
          <button
            className="patria-btn-primary"
            onClick={() => save("submitted")}
            disabled={saving}
            data-testid="mgr-submit"
          >
            <Send className="h-4 w-4" strokeWidth={1.5} /> Submit evaluation
          </button>
        </div>
      )}
    </div>
  );
}
